import { FC, useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { NavItem, SidebarProps } from './Sidebar.types';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { ChevronDown, ChevronRight, MoreHorizontal } from 'lucide-react';
import { sidebarItems } from './SideBatItems';
import { useAppSelector } from '@/core/store/hooks';

export const Sidebar: FC<SidebarProps> = ({ isOpen, onToggleSidebar }) => {
  const user = useAppSelector(state => state.auth.user);
  const location = useLocation();
  const navigate = useNavigate();
  const currentPath = location.pathname;
  const { logout } = useAuth();
  const [showDropdown, setShowDropdown] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const sidebarRef = useRef<HTMLDivElement | null>(null);
  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>({});
  const apiBase = import.meta.env.VITE_API_URL.replace(/\/api$/, "");

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  useEffect(() => {
    const newExpandedItems: Record<string, boolean> = {};
    const expandParentsOfActivePath = (items: NavItem[], path: string): boolean => {
      return items.some(item => {
        const childMatch = item.children?.some(child =>
          (child.path && child.path === path) ||
          (child.path && path.startsWith(child.path + '/')) ||
          (child.children && expandParentsOfActivePath(child.children, path))
        );
        if (childMatch) {
          newExpandedItems[item.label] = true;
          return true;
        }
        return false;
      });
    };
    expandParentsOfActivePath(sidebarItems, currentPath);
    setExpandedItems(prev => ({ ...prev, ...newExpandedItems }));
  }, [currentPath]);

  useEffect(() => {
    if(!isOpen){return}
    const handleClickOutside = (event: MouseEvent) => {
      if (!dropdownRef.current?.contains(event.target as Node) &&
        !buttonRef.current?.contains(event.target as Node)) {
        setShowDropdown(false);
      }
      if (sidebarRef.current && !sidebarRef.current.contains(event.target as Node)) {
        onToggleSidebar();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleLogout = () => {
    logout();
    setShowDropdown(false);
  };

  const toggleExpand = (label: string) => {
    setExpandedItems(prev => ({ ...prev, [label]: !prev[label] }));
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    if (isMobile) {
      onToggleSidebar();
    }
  };

  const NavItemComponent = ({ item, index, level = 0 }: { item: NavItem; index: number; level?: number }) => {
    const hasChildren = Boolean(item.children?.length);
    const isExpanded = expandedItems[item.label];
    const isActive = item.path ?
      (currentPath === item.path || currentPath.startsWith(item.path + '/')) :
      false;

    return (
      <>
        {hasChildren ? (
          <div
            className={`flex items-center w-full px-8 py-3 hover:bg-overlay 
              hover:font-bold transition-colors duration-300 rounded-lg cursor-pointer
              ${isActive ? 'text-primary font-bold' : 'font-normal text-text-secondary'}
              ${level > 0 ? 'pl-[4.2rem]' : ''}`}
            onClick={() => toggleExpand(item.label)}
          >
            <span className={`${isActive ? 'text-primary' : 'text-text-secondary'}`}>{item.icon}</span>
            <span className="text-base whitespace-nowrap leading-tight ml-4">{item.label}</span>
            <span className="ml-auto">
              {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
            </span>
          </div>
        ) : (
          <div
            onClick={() => item.path && handleNavigation(item.path)}
            className={`flex items-center w-full px-8 py-3 hover:bg-overlay 
              hover:font-bold transition-colors duration-300 rounded-lg cursor-pointer
              ${isActive ? 'text-primary font-bold' : 'font-normal text-text-secondary'}
              ${level > 0 ? 'pl-[4.2rem]' : ''}`}
          >
            <span className={`${isActive ? 'text-primary' : 'text-text-secondary'}`}>{item.icon}</span>
            <span className="text-base whitespace-nowrap leading-tight ml-4">{item.label}</span>
          </div>
        )}

        {hasChildren && isExpanded && (
          <div className="flex flex-col w-full">
            {item.children!.map((child, childIndex) => (
              <NavItemComponent
                key={`${index}-${childIndex}`}
                item={child}
                index={childIndex}
                level={level + 1}
              />
            ))}
          </div>
        )}
      </>
    );
  };

  return (
    <>
      {/* Sidebar */}
      <aside
        ref={sidebarRef}
        className={`
          bg-layout border-r border-border overflow-y-auto transition-all duration-300 z-40
          ${isMobile
            ? `fixed top-[6.1875rem] left-0 bottom-[3.75rem] ${isOpen ? 'w-[17.5rem]' : 'w-0'}`
            : `fixed top-[6.1875rem] bottom-[3.75rem] left-0 ${isOpen ? 'w-[20rem]' : 'w-0 overflow-hidden'}`
          }
        `}
      >
        {isOpen && (
          <div className="flex flex-col h-full">
            <div className="flex justify-between items-center px-8 py-6 relative flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-gray-400 flex items-center justify-center overflow-hidden text-white font-bold text-xl">
                  {user?.profileImageUrl ? (<img src={`${apiBase}${user?.profileImageUrl}`} alt="Profile" className="w-full h-full object-cover" />) : (<span>{`${user?.firstName?.[0]?.toUpperCase() || ""}${user?.lastName?.[0]?.toUpperCase() || ""}`}
                  </span>)}

                </div>
                <div className="flex flex-col text-left">
                  <h3 className="text-base font-bold leading-tight text-text-primary">{`${user?.firstName} ${user?.lastName}`}</h3>
                  <p className="text-sm font-normal leading-tight text-text-secondary text-left">{user?.userType}</p>
                </div>
              </div>
              <button
                ref={buttonRef}
                onClick={() => setShowDropdown(!showDropdown)}
                className="text-text-primary hover:text-primary transition-colors"
              >
                <MoreHorizontal size={18} />
              </button>

              {showDropdown && (
                <div
                  ref={dropdownRef}
                  className="absolute right-0 top-16 w-36 bg-white shadow-lg rounded-md py-2 z-50"
                >
                  <div
                    className="flex items-center px-4 py-2 text-text-primary hover:bg-overlay hover:text-primary transition-colors cursor-pointer"
                    onClick={() => {
                      handleNavigation('/profile');
                      setShowDropdown(false);
                    }}
                  >Profile</div>
                  <button
                    className="w-full text-left px-4 py-2 text-text-primary hover:bg-overlay hover:text-primary transition-colors"
                    onClick={handleLogout}
                  >Logout</button>
                </div>
              )}
            </div>

            <div className="flex flex-col py-4 space-y-4 overflow-y-auto flex-1">
              {sidebarItems.map((item, index) => (

                <div key={index} className="flex flex-col w-full">

                  <NavItemComponent item={item} index={index} />

                  {item.dividerAfter && (
                    <div className="border-b border-gray-200 my-2" />
                  )}
                </div>
              ))}
              {/* {sidebarItems.map((item, index) => (
                <div key={index} className="flex flex-col w-full">
                  <NavItemComponent item={item} index={index} />
                </div>
              ))} */}
            </div>
          </div>
        )}
      </aside>

      {/* Hamburger button */}
      <button
        onClick={onToggleSidebar}
        className={`
          fixed flex flex-col items-center justify-center
          w-6 h-6 bg-layout rounded-tr-lg rounded-br-lg border-0 outline-none
          transition-all duration-300 z-50 hover:bg-overlay
          ${isOpen
            ? (isMobile ? 'left-[17.5rem]' : 'left-[20rem]')
            : 'left-0'
          }
        `}
        style={{ top: isMobile ? '7rem' : '6.1875rem' }}
        aria-label="Toggle sidebar"
      >
        <div className="flex flex-col items-center justify-center space-y-[3px]">
          <div className="w-4 h-[1.5px] bg-primary"></div>
          <div className="w-4 h-[1.5px] bg-primary"></div>
          <div className="w-4 h-[1.5px] bg-primary"></div>
        </div>
      </button>
    </>
  );
};

export default Sidebar;